import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Newspaper, Plus, Trash2, Pencil, ExternalLink, Sparkles, Image as ImageIcon, Bot, Power, RefreshCw, Search, CalendarClock, X, Megaphone, Facebook, Users, Eye, Share2, Upload } from "lucide-react";
import ShareButton from "@/components/ShareButton";
import FbGroupShare from "@/components/FbGroupShare";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", cover_image: "", city: "", category: "", meta_description: "", status: "draft", author_name: "" };

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [regenId, setRegenId] = useState(null);
  const [editing, setEditing] = useState(null); // null | {id?, ...}
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState({ city: "", category: "", style: "photo", busy: false });
  const [imgBusy, setImgBusy] = useState(false);
  const [coverUp, setCoverUp] = useState(false);
  const coverRef = useRef(null);
  const onPickCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCoverUp(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/blog/upload-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Image de couverture importée.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'import de l'image.");
    } finally {
      setCoverUp(false);
    }
  };
  const [kw, setKw] = useState({ list: [], input: "", busy: false });
  const [auto, setAuto] = useState(null);
  const [autoRun, setAutoRun] = useState(false);
  const [filters, setFilters] = useState({ q: "", status: "all", city: "all", category: "all", source: "all" });
  const [shareItem, setShareItem] = useState(null);
  const [stats, setStats] = useState(null);
  const [sortBy, setSortBy] = useState(null);

  const fmtDate = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    if (isNaN(d)) return "—";
    return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
  };

  const timeAgo = (s) => {
    if (!s) return "jamais";
    const diff = Date.now() - new Date(s).getTime();
    if (isNaN(diff)) return "—";
    const m = Math.floor(diff / 60000);
    if (m < 1) return "à l'instant";
    if (m < 60) return `il y a ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `il y a ${h} h`;
    const j = Math.floor(h / 24);
    return `il y a ${j} j`;
  };

  const timeUntil = (s) => {
    if (!s) return "—";
    const diff = new Date(s).getTime() - Date.now();
    if (isNaN(diff) || diff <= 0) return "imminent";
    const m = Math.floor(diff / 60000);
    if (m < 60) return `dans ${m} min`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return rm ? `dans ${h} h ${rm} min` : `dans ${h} h`;
  };

  const selectedKeywords = () => kw.list.filter((k) => k.on).map((k) => k.text).join(", ");

  const genKeywords = async () => {
    if (!gen.city.trim() || !gen.category.trim()) { toast.error("Indiquez une ville/département et une catégorie."); return; }
    setKw((k) => ({ ...k, busy: true }));
    try {
      const { data } = await api.post("/admin/blog/keywords", { city: gen.city, category: gen.category });
      const list = (data.keywords || []).map((t) => ({ text: t, on: true }));
      setKw((k) => ({ ...k, list, busy: false }));
      toast.success(`${list.length} mots-clés générés — ajustez puis générez le brouillon.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur mots-clés"); setKw((k) => ({ ...k, busy: false })); }
  };

  const toggleKw = (i) => setKw((k) => ({ ...k, list: k.list.map((x, j) => (j === i ? { ...x, on: !x.on } : x)) }));
  const addKw = () => { const t = kw.input.trim(); if (!t) return; setKw((k) => ({ ...k, list: [...k.list, { text: t, on: true }], input: "" })); };

  const generate = async () => {
    if (!gen.city.trim() || !gen.category.trim()) { toast.error("Indiquez une ville et une catégorie."); return; }
    setGen((g) => ({ ...g, busy: true }));
    try {
      const { data } = await api.post("/admin/blog/generate", { city: gen.city, category: gen.category, keywords: selectedKeywords(), style: gen.style });
      toast.info("Génération lancée (rédaction + images, ~1 min)… vous pouvez patienter ici.");
      const result = await pollBlogJob(data.job_id);
      setForm((f) => ({ ...f, title: result.title, excerpt: result.excerpt, content: result.content,
        meta_description: result.meta_description, cover_image: result.cover_image || f.cover_image,
        city: result.city, category: result.category, author_name: result.author_name || f.author_name, status: "draft" }));
      const sc = result?.utility?.score;
      toast.success(typeof sc === "number"
        ? `Brouillon généré — score d'utilité ${sc}/100. Relisez puis publiez.`
        : "Brouillon généré ! Relisez puis publiez.");
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Erreur de génération"); }
    finally { setGen((g) => ({ ...g, busy: false })); }
  };

  const regenerate = async (p) => {
    if (!window.confirm(`Régénérer « ${p.title} » ?\nL'IA réécrira cet article en citant vos vrais partenaires, avec une vraie photo libre de droits et un nouveau score d'utilité. Le lien (slug) est conservé.`)) return;
    setRegenId(p.id);
    try {
      const { data } = await api.post(`/admin/blog/${p.id}/regenerate`);
      toast.info("Régénération lancée (~1 min)…");
      await pollBlogJob(data.job_id);
      await load();
      toast.success("Article régénéré ✅");
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Erreur de régénération"); }
    finally { setRegenId(null); }
  };

  const pollBlogJob = (jobId) => new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = async () => {
      if (Date.now() - started > 240000) { reject(new Error("La génération prend trop de temps, réessayez.")); return; }
      try {
        const { data } = await api.get(`/admin/blog/generate/status/${jobId}`);
        if (data.status === "done") { resolve(data.result); return; }
        if (data.status === "error") { reject(new Error(data.error || "Erreur de génération")); return; }
        setTimeout(tick, 4000);
      } catch (err) { reject(new Error(err?.response?.data?.detail || "Erreur de suivi de génération")); }
    };
    setTimeout(tick, 4000);
  });

  const regenImage = async () => {
    const city = (form.city || gen.city).trim();
    const category = (form.category || gen.category).trim();
    if (!city || !category) { toast.error("Renseignez la ville et la catégorie d'abord."); return; }
    setImgBusy(true);
    try {
      const { data } = await api.post("/admin/blog/generate-image", { city, category, keywords: selectedKeywords(), style: gen.style });
      setForm((f) => ({ ...f, cover_image: data.cover_image }));
      toast.success("Photo libre de droits sélectionnée !");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de génération d'image"); }
    finally { setImgBusy(false); }
  };

  const load = () => api.get("/admin/blog").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  const loadAuto = () => api.get("/admin/blog/auto/settings").then((r) => setAuto(r.data)).catch(() => {});
  const loadStats = () => api.get("/admin/blog/stats?days=30").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); loadAuto(); loadStats(); }, []);

  const toggleAuto = async () => {
    const next = !auto.enabled;
    try {
      const { data } = await api.post("/admin/blog/auto/settings", { enabled: next });
      setAuto((a) => ({ ...a, ...data }));
      toast.success(next ? "Blog automatique activé" : "Blog automatique désactivé");
    } catch { toast.error("Erreur de mise à jour"); }
  };

  const setPerDay = async (n) => {
    try {
      const { data } = await api.post("/admin/blog/auto/settings", { per_day: n });
      setAuto((a) => ({ ...a, ...data }));
      toast.success(`${n} article${n > 1 ? "s" : ""} par jour`);
    } catch { toast.error("Erreur de mise à jour"); }
  };

  const runAutoNow = async () => {
    setAutoRun(true);
    try {
      await api.post("/admin/blog/auto/run");
      toast.success("Génération lancée — l'article apparaîtra dans ~30 s.");
      setTimeout(load, 35000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setTimeout(() => setAutoRun(false), 4000); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = async (id) => {
    const { data } = await api.get(`/admin/blog/${id}`);
    setForm({ ...EMPTY, ...data });
    setEditing(data);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Le titre est requis."); return; }
    if (form.status === "published" && !form.cover_image.trim()) {
      toast.error("Une image de couverture est requise pour publier (choisissez une photo libre de droits ou collez une URL).");
      return;
    }
    if (form.status === "published" && typeof form.utility_score === "number" && form.utility_score < 80) {
      if (!window.confirm(`⚠️ Score d'utilité de ${form.utility_score}/100 (seuil conseillé : 80).\nCet article manque sans doute de valeur locale concrète (acteurs réels cités, structure, longueur). Publier quand même ?`)) return;
    }
    setBusy(true);
    try {
      if (editing?.id) await api.patch(`/admin/blog/${editing.id}`, form);
      else await api.post("/admin/blog", form);
      toast.success("Article enregistré");
      setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/admin/blog/${id}`); toast.success("Supprimé"); load();
  };

  const cities = [...new Set(items.map((i) => i.city).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr"));
  const categories = [...new Set(items.map((i) => i.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr"));
  const filtered = items.filter((p) => {
    if (filters.status !== "all" && p.status !== filters.status) return false;
    if (filters.city !== "all" && p.city !== filters.city) return false;
    if (filters.category !== "all" && p.category !== filters.category) return false;
    if (filters.source === "auto" && !p.auto_generated) return false;
    if (filters.source === "manual" && p.auto_generated) return false;
    if (filters.q && !(p.title || "").toLowerCase().includes(filters.q.toLowerCase())) return false;
    return true;
  });
  const resetFilters = () => setFilters({ q: "", status: "all", city: "all", category: "all", source: "all" });
  const hasActiveFilters = filters.q || filters.status !== "all" || filters.city !== "all" || filters.category !== "all" || filters.source !== "all";
  const displayed = sortBy ? [...filtered].sort((a, b) => ((b[sortBy] || 0) - (a[sortBy] || 0))) : filtered;

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-heading text-2xl font-extrabold text-slate-900 flex items-center gap-2"><Newspaper size={22} className="text-[#E05638]" /> Blog local</h1>
        {!editing && <Button onClick={openNew} className="btn-shine text-white" data-testid="admin-blog-new"><Plus size={16} className="mr-1.5" /> Nouvel article</Button>}
      </div>

      {!editing && (
        <div className="mt-6 rounded-2xl border border-indigo-200 bg-indigo-50/50 p-5" data-testid="admin-auto-blog-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900"><Bot size={18} className="text-indigo-600" /> Blog automatique (IA)</div>
              <p className="text-xs text-slate-600 mt-1 max-w-xl">Génère des <strong>brouillons</strong> SEO locaux (activité × communes d'Eure-et-Loir, vraies photos libres de droits). Chaque brouillon reçoit un <strong>score d'utilité /100</strong> ; en dessous de 80 il est automatiquement rejeté. Aucune publication sans votre validation humaine.</p>
            </div>
            {auto && (
              <button type="button" onClick={toggleAuto}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold border transition-colors ${auto.enabled ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-300"}`}
                data-testid="admin-auto-blog-toggle">
                <Power size={14} /> {auto.enabled ? "Activé" : "Désactivé"}
              </button>
            )}
          </div>
          <div className="mt-3 flex items-center gap-4 flex-wrap">
            <Button type="button" variant="outline" size="sm" onClick={runAutoNow} disabled={autoRun} data-testid="admin-auto-blog-run">
              <RefreshCw size={14} className={`mr-1.5 ${autoRun ? "animate-spin" : ""}`} /> {autoRun ? "Génération lancée…" : "Générer un article maintenant"}
            </Button>
            {auto && (
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-slate-600">Articles par jour :</label>
                <select value={auto.per_day || 3} onChange={(e) => setPerDay(Number(e.target.value))}
                  className="border border-slate-300 rounded-lg px-2 py-1 text-sm" data-testid="admin-auto-blog-perday">
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
          </div>
          {auto && (
            <div className="mt-3 grid sm:grid-cols-3 gap-2" data-testid="admin-auto-blog-status">
              <div className="rounded-xl bg-white border border-indigo-100 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Dernier article auto</div>
                <div className="text-sm font-bold text-slate-900" data-testid="admin-auto-blog-last">{timeAgo(auto.last_auto_at)}</div>
                {auto.last_auto_at && <div className="text-[11px] text-slate-500 truncate">{fmtDate(auto.last_auto_at)}</div>}
              </div>
              <div className="rounded-xl bg-white border border-indigo-100 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Aujourd'hui</div>
                <div className="text-sm font-bold text-slate-900" data-testid="admin-auto-blog-today">{auto.today_count ?? 0} / {auto.per_day || 3}</div>
                <div className="text-[11px] text-slate-500">article(s) généré(s)</div>
              </div>
              <div className="rounded-xl bg-white border border-indigo-100 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Prochain passage</div>
                <div className="text-sm font-bold text-slate-900" data-testid="admin-auto-blog-next">{auto.enabled ? timeUntil(auto.next_run_at) : "désactivé"}</div>
                <div className="text-[11px] text-slate-500">{auto.enabled ? fmtDate(auto.next_run_at) : "activez le blog auto"}</div>
              </div>
            </div>
          )}
          {auto && (
            <p className="mt-2 text-[11px] text-amber-700" data-testid="admin-auto-blog-prod-note">
              ⚠️ La génération automatique ne s'exécute que sur le site <strong>publié (production)</strong>, pas en prévisualisation. Utilisez « Générer un article maintenant » pour un essai immédiat.
            </p>
          )}
        </div>
      )}

      {editing ? (
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="admin-blog-form">
          <div className="rounded-xl border border-dashed border-[#E05638]/40 bg-orange-50/40 p-4" data-testid="blog-ai-panel">
            <div className="flex items-center gap-2 font-semibold text-slate-900 text-sm"><Sparkles size={16} className="text-[#E05638]" /> Générer un brouillon par IA</div>
            <p className="text-xs text-slate-500 mt-1">Saisissez une ville (ou un département) et une catégorie. L'IA propose 10 mots-clés locaux ; ajustez-les, ajoutez les vôtres, puis générez le brouillon.</p>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              <div><Label>Ville ou département</Label><Input value={gen.city} onChange={(e) => setGen((g) => ({ ...g, city: e.target.value }))} placeholder="Chartres ou Eure-et-Loir" data-testid="blog-ai-city" /></div>
              <div><Label>Catégorie / activité</Label><Input value={gen.category} onChange={(e) => setGen((g) => ({ ...g, category: e.target.value }))} placeholder="Restaurants" data-testid="blog-ai-category" /></div>
            </div>
            <div className="mt-3">
              <Button type="button" variant="outline" onClick={genKeywords} disabled={kw.busy} data-testid="blog-ai-keywords-btn"><Sparkles size={14} className="mr-1.5" />{kw.busy ? "Génération…" : "1. Générer 10 mots-clés"}</Button>
            </div>
            {kw.list.length > 0 && (
              <div className="mt-3" data-testid="blog-ai-keywords-list">
                <div className="flex flex-wrap gap-1.5">
                  {kw.list.map((k, i) => (
                    <button type="button" key={i} onClick={() => toggleKw(i)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${k.on ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"}`}
                      data-testid={`blog-kw-${i}`}>{k.text}</button>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Input value={kw.input} onChange={(e) => setKw((k) => ({ ...k, input: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKw(); } }} placeholder="Ajouter un mot-clé…" className="max-w-xs" data-testid="blog-kw-input" />
                  <Button type="button" variant="outline" size="sm" onClick={addKw} data-testid="blog-kw-add"><Plus size={14} className="mr-1" /> Ajouter</Button>
                  <span className="text-xs text-slate-400">{kw.list.filter((k) => k.on).length} sélectionné(s)</span>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Button type="button" onClick={generate} disabled={gen.busy} className="btn-shine text-white" data-testid="blog-ai-generate">{gen.busy ? "Génération…" : "2. Générer le brouillon"}</Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Titre</Label><Input value={form.title} onChange={set("title")} data-testid="blog-title" placeholder="Les 5 meilleurs restaurants à Chartres en 2026" /></div>
            <div><Label>Slug (laisser vide = auto)</Label><Input value={form.slug} onChange={set("slug")} placeholder="restaurants-chartres-2026" /></div>
            <div><Label>Ville (facultatif)</Label><Input value={form.city} onChange={set("city")} placeholder="Chartres" /></div>
            <div><Label>Catégorie (facultatif)</Label><Input value={form.category} onChange={set("category")} placeholder="Restaurants" /></div>
            <div className="sm:col-span-2"><Label>Image de couverture (URL) <span className="text-[#E05638]">· requise pour publier</span></Label>
              <Input value={form.cover_image} onChange={set("cover_image")} placeholder="https://…" data-testid="blog-cover-url" />
              {!form.cover_image.trim() && (
                <p className="mt-1 text-xs text-amber-700">Une image de couverture est obligatoire pour publier l'article (pour un bel aperçu sur les réseaux).</p>
              )}
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <Button type="button" variant="outline" size="sm" onClick={regenImage} disabled={imgBusy} data-testid="blog-regen-image"><ImageIcon size={14} className="mr-1.5" /> {imgBusy ? "Sélection…" : "Choisir une photo libre de droits"}</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => coverRef.current?.click()} disabled={coverUp} data-testid="blog-cover-upload-btn"><Upload size={14} className="mr-1.5" /> {coverUp ? "Import…" : "Importer une image"}</Button>
                <input ref={coverRef} type="file" accept="image/*" hidden onChange={onPickCover} data-testid="blog-cover-upload-input" />
                {form.cover_image && <img src={form.cover_image} alt="" className="h-12 w-20 object-cover rounded border border-slate-200" />}
              </div>
            </div>
          </div>
          <div><Label>Accroche (excerpt)</Label><Textarea value={form.excerpt} onChange={set("excerpt")} rows={2} data-testid="blog-excerpt" /></div>
          <div><Label>Contenu (utilisez « ## » pour un sous-titre, double saut de ligne pour un paragraphe)</Label>
            <Textarea value={form.content} onChange={set("content")} rows={12} className="font-mono text-sm" data-testid="blog-content" /></div>
          <div><Label>Meta description (SEO)</Label><Input value={form.meta_description} onChange={set("meta_description")} maxLength={300} /></div>
          <div className="flex items-center gap-3">
            <Label className="mb-0">Statut</Label>
            <select value={form.status} onChange={set("status")} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="blog-status">
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy} className="btn-shine text-white" data-testid="blog-save">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
            <Button variant="outline" onClick={() => { setEditing(null); setForm(EMPTY); }}>Annuler</Button>
          </div>
        </div>
      ) : (
        <>
          {stats && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid="admin-blog-dashboard">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Performance · {stats.articles} article(s) sur 30 jours
                </div>
                {sortBy && (
                  <button onClick={() => setSortBy(null)} data-testid="admin-blog-sort-reset"
                    className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-[#E05638]">
                    <X size={12} /> Tri actif : {sortBy === "views" ? "ouvertures" : "partages"} — réinitialiser
                  </button>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <button onClick={() => setSortBy(sortBy === "views" ? null : "views")} data-testid="admin-blog-dash-views"
                  className={`rounded-xl p-3 text-center transition-all ${sortBy === "views" ? "bg-rose-50 ring-2 ring-[#E05638]" : "bg-slate-50 hover:bg-slate-100"}`}>
                  <div className="flex items-center justify-center gap-1.5 text-slate-400 text-xs font-semibold"><Eye size={13} /> Ouvertures</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{stats.views}</div>
                </button>
                <button onClick={() => setSortBy(sortBy === "shares" ? null : "shares")} data-testid="admin-blog-dash-shares"
                  className={`rounded-xl p-3 text-center transition-all ${sortBy === "shares" ? "bg-rose-50 ring-2 ring-[#E05638]" : "bg-slate-50 hover:bg-slate-100"}`}>
                  <div className="flex items-center justify-center gap-1.5 text-slate-400 text-xs font-semibold"><Share2 size={13} /> Partages</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{stats.shares}</div>
                </button>
              </div>
            </div>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="admin-blog-filters">
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Rechercher un titre…" className="pl-8" data-testid="blog-filter-search" />
            </div>
            <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-white" data-testid="blog-filter-status">
              <option value="all">Tous les statuts</option>
              <option value="published">Publié</option>
              <option value="draft">Brouillon</option>
            </select>
            <select value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))} className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-white" data-testid="blog-filter-source">
              <option value="all">Toutes sources</option>
              <option value="auto">Généré par IA</option>
              <option value="manual">Rédigé à la main</option>
            </select>
            <select value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))} className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-white" data-testid="blog-filter-city">
              <option value="all">Toutes les villes</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-white" data-testid="blog-filter-category">
              <option value="all">Toutes les catégories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-slate-500" data-testid="blog-filter-reset"><X size={14} className="mr-1" /> Réinitialiser</Button>
            )}
            <span className="text-xs text-slate-400 ml-auto" data-testid="blog-filter-count">{filtered.length} / {items.length} article{items.length > 1 ? "s" : ""}</span>
          </div>

          <div className="mt-3 bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {items.length === 0 ? (
              <div className="p-10 text-center text-slate-500" data-testid="admin-blog-empty">Aucun article. Créez le premier !</div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center text-slate-500" data-testid="admin-blog-no-results">Aucun article ne correspond à ces filtres.</div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="admin-blog-list">
                {displayed.map((p) => {
                  const fbShared = (p.shares_by?.facebook || 0) > 0;
                  const grpShared = (p.shares_by?.fb_groups || 0) > 0;
                  return (
                  <li key={p.id} data-testid={`blog-row-${p.slug}`}
                    className={`p-4 flex items-center justify-between gap-3 border-l-4 ${fbShared || grpShared ? "border-l-emerald-400 bg-emerald-50/40" : "border-l-transparent"}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{p.title}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                        <span className={`px-2 py-0.5 rounded-full font-semibold ${p.status === "published" ? "bg-emerald-100 text-emerald-800" : (p.auto_generated ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600")}`} data-testid={`blog-status-badge-${p.slug}`}>{p.status === "published" ? "Publié" : (p.auto_generated ? "À valider" : "Brouillon")}</span>
                        {p.auto_generated && <span className="px-2 py-0.5 rounded-full font-semibold bg-indigo-100 text-indigo-700">IA</span>}
                        {typeof p.utility_score === "number" && (
                          <span data-testid={`blog-score-${p.slug}`} title={(p.score_reasons || []).join(" · ") || "Score d'utilité"}
                            className={`px-2 py-0.5 rounded-full font-semibold ${p.utility_score >= 80 ? "bg-emerald-100 text-emerald-800" : p.utility_score >= 60 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700"}`}>
                            Score {p.utility_score}/100
                          </span>
                        )}
                        {fbShared && <span data-testid={`blog-badge-fb-${p.slug}`} className="px-2 py-0.5 rounded-full font-semibold bg-blue-100 text-[#1877F2] inline-flex items-center gap-1"><Facebook size={11} /> Partagé sur Facebook</span>}
                        {grpShared && <span data-testid={`blog-badge-grp-${p.slug}`} className="px-2 py-0.5 rounded-full font-semibold bg-orange-100 text-[#E05638] inline-flex items-center gap-1"><Megaphone size={11} /> Partagé en groupes</span>}
                        <span className="inline-flex items-center gap-1 text-slate-600" data-testid={`blog-date-${p.slug}`}><CalendarClock size={12} /> {fmtDate(p.created_at)}</span>
                        <span>· /blog/{p.slug}</span>
                        {p.city && <span>· {p.city}</span>}
                        <span>· {(p.views || 0)} vue{(p.views || 0) > 1 ? "s" : ""}</span>
                        <span>· {(p.shares || 0)} partage{(p.shares || 0) > 1 ? "s" : ""}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {p.status === "published" && (
                        <ShareButton variant="icon" url={`${process.env.REACT_APP_BACKEND_URL}/api/blog-vitrine/${p.slug}`}
                          title={p.title} text={p.title} testId={`blog-share-${p.slug}`}
                          onShare={(ch) => api.post(`/blog/${p.slug}/share`, { channel: ch }).then(load).catch(() => {})} />
                      )}
                      {p.status === "published" && (
                        <Button variant="ghost" size="sm" onClick={() => setShareItem(p)} title="Partager dans des groupes Facebook"
                          data-testid={`blog-fbgroups-${p.slug}`} className="text-[#1877F2] gap-1 px-2">
                          <Users size={15} /><span className="text-xs font-semibold">Partager dans des groupes</span></Button>
                      )}
                      {p.status === "published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-slate-500 hover:text-[#E05638]"><ExternalLink size={16} /></a>}
                      <Button variant="ghost" size="sm" disabled={regenId === p.id} onClick={() => regenerate(p)} title="Régénérer avec le nouveau style (sans gras) + vrais partenaires" data-testid={`blog-regenerate-${p.slug}`} className="text-indigo-600"><Sparkles size={15} className={regenId === p.id ? "animate-pulse" : ""} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p.id)} data-testid={`blog-edit-${p.slug}`}><Pencil size={15} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-rose-600" data-testid={`blog-delete-${p.slug}`}><Trash2 size={15} /></Button>
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}

      <Dialog open={!!shareItem} onOpenChange={(o) => { if (!o) setShareItem(null); }}>
        <DialogContent className="max-w-lg" data-testid="blog-fbgroups-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Megaphone size={18} className="text-[#1877F2]" /> Partager dans des groupes Facebook</DialogTitle>
            <DialogDescription className="truncate">{shareItem?.title}</DialogDescription>
          </DialogHeader>
          {shareItem && (
            <FbGroupShare
              text={shareItem.title}
              shareUrl={`${process.env.REACT_APP_BACKEND_URL}/api/blog-vitrine/${shareItem.slug}`}
              cityHint={shareItem.city || ""}
              onShared={() => api.post(`/blog/${shareItem.slug}/share`, { channel: "fb_groups" }).then(load).catch(() => {})}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
